// Tooltip — minimal inline help affordance (Stage 16). Presentation, no deps.
//
// Renders a "?" button that exposes a small popover on hover, focus, and click.
// Designed for surfacing 1–2 sentence contextual help inferred from the engine
// layer (e.g. explaining what "DLOM" means next to its input). No external
// library; closes on Escape / outside click and follows the user's reduced-
// motion preference by leaving the popover always visible while open.

import { useCallback, useEffect, useId, useReducer, useRef, type ReactNode } from 'react';

interface TooltipProps {
  /** Plain-text help content. Keep to ≤ 2 sentences. */
  label: string;
  /** Optional accessible name for the trigger; defaults to "Help". */
  ariaLabel?: string;
  /** Override the inline trigger glyph. */
  children?: ReactNode;
}

export function Tooltip({ label, ariaLabel = 'Help', children }: TooltipProps) {
  const [open, toggle] = useReducer((s: boolean) => !s, false);

  // Mirror open into a ref so the close handlers can stay identity-stable
  // without re-subscribing listeners every render.
  const openRef = useRef(open);
  openRef.current = open;
  const closeIfOpen = useCallback(() => {
    if (openRef.current) toggle();
  }, []);

  const triggerId = useId();
  const popoverId = useId();
  const wrapRef = useRef<HTMLSpanElement>(null);

  // Outside-click + Escape handling for the click-to-open path.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) closeIfOpen();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeIfOpen();
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, closeIfOpen]);

  return (
    <span
      className="help-tip"
      ref={wrapRef}
      onMouseEnter={() => !open && toggle()}
      onMouseLeave={closeIfOpen}
    >
      <button
        type="button"
        id={triggerId}
        className="help-tip-trigger"
        aria-label={ariaLabel}
        aria-describedby={open ? popoverId : undefined}
        aria-expanded={open}
        onClick={toggle}
        onFocus={() => !open && toggle()}
        onBlur={closeIfOpen}
      >
        {children ?? '?'}
      </button>
      {open && (
        <span id={popoverId} role="tooltip" className="help-tip-popover">
          {label}
        </span>
      )}
    </span>
  );
}
